// Registry of contextual-help anchor keys.
//
// Each anchor is a stable identifier for a page. Admins attach help articles to
// these keys; the route-aware HelpButton resolves the current path to an anchor
// via getHelpContextForRoute (below) and pulls the visible articles for it.
// Keep keys stable once shipped so existing content keeps resolving.

// Special placement: articles here are not shown on any page's "?" button but
// drive the post-login Welcome intro-video modal.
export const WELCOME_ANCHOR = 'welcome.intro';

// Help content can only be attached to these customer-facing app pages.
export const HELP_ANCHORS = Object.freeze({
    'welcome.intro': 'Welcome (Intro Video)',
    'documents.overview': 'Documents',
    'members.overview': 'Client Member List',
    'settings.overview': 'Authentication Settings',
    'conversations.history': 'Conversation History',
    'analytics.overview': 'Analytics',
    'chatbots.settings': 'Chatbots',
    'agents.overview': 'Agents',
    'agent.requests': 'Live Agents',
    'tickets.overview': 'Tickets',
});

// [{ key, label }] sorted by label, for admin multi-select dropdowns.
export const helpAnchorOptions = Object.entries(HELP_ANCHORS)
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

export const anchorLabel = (key) => HELP_ANCHORS[String(key ?? '').trim()] || String(key ?? '');

// Display order for the customer Help Center — Welcome first, then the same
// order the pages appear in the sidebar. Keep in sync with Sidebar.js nav.
export const HELP_ANCHOR_ORDER = [
    WELCOME_ANCHOR,
    'documents.overview',
    'members.overview',
    'settings.overview',
    'conversations.history',
    'analytics.overview',
    'chatbots.settings',
    'agents.overview',
    'agent.requests',
    'tickets.overview',
];

// Index for sorting help sections; unknown anchors sort after known ones.
export const anchorOrderIndex = (key) => {
    const i = HELP_ANCHOR_ORDER.indexOf(String(key ?? '').trim());
    return i === -1 ? HELP_ANCHOR_ORDER.length : i;
};

// Route → help mapping. A single source of truth so the contextual HelpButton
// is self-aware of which page it is on — pages don't pass an anchor. Only the
// routes listed here get a "?"; every other authenticated route resolves to
// null and the button renders nothing. More specific prefixes come first.
const ROUTE_HELP = [
    { prefix: '/documents', anchor: 'documents.overview', label: 'Documents' },
    { prefix: '/chatbot-settings', anchor: 'chatbots.settings', label: 'Chatbots' },
    { prefix: '/member-list', anchor: 'members.overview', label: 'Client Member List' },
    { prefix: '/nchistory', anchor: 'conversations.history', label: 'Conversation History' },
    { prefix: '/nanalytics', anchor: 'analytics.overview', label: 'Analytics' },
    { prefix: '/agent-requests', anchor: 'agent.requests', label: 'Live Agents' },
    { prefix: '/agents', anchor: 'agents.overview', label: 'Agents' },
    { prefix: '/tickets', anchor: 'tickets.overview', label: 'Tickets' },
    { prefix: '/settings', anchor: 'settings.overview', label: 'Authentication Settings' },
];

export function getHelpContextForRoute(pathname) {
    const p = String(pathname ?? '');
    const match = ROUTE_HELP.find((r) => p === r.prefix || p.startsWith(`${r.prefix}/`));
    return match ? { anchor: match.anchor, label: match.label } : null;
}
